import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AlertCircle, MessageCircle, Copy, Send, Phone, RefreshCw, Mail } from 'lucide-react';

const TIER_META = {
  friendly: { label: 'Friendly', color: 'bg-success/20 text-success border-success/30' },
  firm: { label: 'Firm', color: 'bg-warning/20 text-warning border-warning/30' },
  final: { label: 'Final Notice', color: 'bg-error/20 text-error border-error/30' },
};

export default function SmartDunningPage() {
  const [loading, setLoading] = useState(false);
  const [minDays, setMinDays] = useState(1);
  const [reminders, setReminders] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedMessages, setEditedMessages] = useState({});

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const res = await api.post('/invoices/smart-dunning', { min_days_overdue: parseInt(minDays) || 0 });
      setReminders(res.data.reminders || []);
      setTotalOutstanding(res.data.total_outstanding || 0);
    } catch (err) {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReminders(); /* eslint-disable-next-line */ }, []);

  const copyMessage = (idx) => {
    const msg = editedMessages[idx] ?? reminders[idx].message;
    navigator.clipboard.writeText(msg);
    toast.success('Message copied');
  };

  const openWhatsApp = (idx) => {
    const r = reminders[idx];
    if (!r.phone) { toast.error('No phone on file'); return; }
    const msg = editedMessages[idx] ?? r.message;
    const phoneDigits = (r.phone || '').replace(/\D/g, '');
    window.open(`https://wa.me/${phoneDigits}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const byTier = reminders.reduce((acc, r) => {
    acc[r.tier] = (acc[r.tier] || 0) + 1;
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="smart-dunning-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit flex items-center gap-2">
              <AlertCircle className="text-error" size={26} /> Smart Dunning
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Auto-composed reminders for overdue credit invoices, tiered by severity.</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[11px] text-muted-foreground">Min days overdue</Label>
              <Input type="number" min="0" value={minDays} onChange={(e) => setMinDays(e.target.value)} className="w-24 h-9" data-testid="min-days-input" />
            </div>
            <Button onClick={fetchReminders} disabled={loading} className="h-9" data-testid="refresh-dunning-btn">
              <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Customers to Remind</div><div className="text-2xl font-bold font-outfit mt-1">{reminders.length}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Total Outstanding</div><div className="text-2xl font-bold font-outfit mt-1 text-error">{cur()} {totalOutstanding.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Friendly / Firm</div><div className="text-xl font-bold mt-1"><span className="text-success">{byTier.friendly || 0}</span> / <span className="text-warning">{byTier.firm || 0}</span></div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Final Notices</div><div className="text-2xl font-bold font-outfit mt-1 text-error">{byTier.final || 0}</div></CardContent></Card>
        </div>

        {loading && <div className="text-center text-muted-foreground py-12">Loading…</div>}

        {!loading && reminders.length === 0 && (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            <AlertCircle className="mx-auto mb-3 text-success" size={36} />
            No overdue invoices. You&rsquo;re all caught up.
          </CardContent></Card>
        )}

        {!loading && reminders.map((r, idx) => {
          const meta = TIER_META[r.tier] || TIER_META.friendly;
          const isEditing = editingIndex === idx;
          return (
            <Card key={idx} className="border-l-4" style={{ borderLeftColor: r.tier === 'final' ? '#e11d48' : r.tier === 'firm' ? '#d97706' : '#10b981' }}>
              <CardHeader className="flex flex-row items-start justify-between pb-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2 font-outfit">
                    {r.customer_name}
                    <Badge className={`text-[10px] border ${meta.color}`}>{meta.label}</Badge>
                    <span className="text-[11px] text-muted-foreground">• {r.max_days_overdue}d overdue • {r.invoice_count} invoice{r.invoice_count !== 1 ? 's' : ''}</span>
                  </CardTitle>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    {r.phone && <span className="flex items-center gap-1"><Phone size={11} /> {r.phone}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-muted-foreground uppercase">Outstanding</div>
                  <div className="text-lg font-bold text-error">{cur()} {r.total_outstanding.toLocaleString()}</div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                {isEditing ? (
                  <Textarea
                    rows={8}
                    value={editedMessages[idx] ?? r.message}
                    onChange={(e) => setEditedMessages({ ...editedMessages, [idx]: e.target.value })}
                    className="text-xs font-mono bg-muted/50"
                  />
                ) : (
                  <pre className="text-xs whitespace-pre-wrap bg-muted/50 border border-border rounded-lg p-3 font-mono leading-relaxed">{editedMessages[idx] ?? r.message}</pre>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setEditingIndex(isEditing ? null : idx)} data-testid={`edit-msg-btn-${idx}`}>
                    {isEditing ? 'Done' : 'Edit'}
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => copyMessage(idx)} data-testid={`copy-msg-btn-${idx}`}>
                    <Copy size={12} className="mr-1" /> Copy
                  </Button>
                  {r.phone && (
                    <Button size="sm" className="h-8 bg-success hover:bg-emerald-700 text-white" onClick={() => openWhatsApp(idx)} data-testid={`wa-btn-${idx}`}>
                      <MessageCircle size={12} className="mr-1" /> WhatsApp
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-8 ml-auto" disabled title="Email integration coming soon">
                    <Mail size={12} className="mr-1" /> Email
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
